/**
 * Web Push 通知のサブスクリプション管理 hook
 */

import { useCallback, useEffect, useState } from 'react'

import { getVapidPublicKey, subscribePush, unsubscribePush } from '@/api/services/push'

type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported'

/**
 * Base64url 文字列を Uint8Array に変換 (applicationServerKey 用)
 */
function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray.buffer
}

export function usePushNotification() {
  const [permission, setPermission] = useState<PushPermissionState>('default')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // 初期状態チェック
  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setPermission('unsupported')
      return
    }
    setPermission(Notification.permission as PushPermissionState)

    // 既存のサブスクリプションを確認
    navigator.serviceWorker.ready.then((registration) => {
      registration.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(sub !== null)
      })
    })
  }, [])

  /** 通知を有効化 (許可リクエスト + サブスクリプション登録) */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return false

    setIsLoading(true)
    try {
      // 1. 通知許可リクエスト
      const result = await Notification.requestPermission()
      setPermission(result as PushPermissionState)
      if (result !== 'granted') return false

      // 2. VAPID public key 取得
      const vapidPublicKey = await getVapidPublicKey()

      // 3. PushManager でサブスクリプション作成
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(vapidPublicKey),
      })

      // 4. サーバーに登録
      await subscribePush(subscription)
      setIsSubscribed(true)
      return true
    } catch (error) {
      console.error('Push subscription failed:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  /** 通知を無効化 */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await unsubscribePush(subscription)
        await subscription.unsubscribe()
      }
      setIsSubscribed(false)
      return true
    } catch (error) {
      console.error('Push unsubscribe failed:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    isSupported: permission !== 'unsupported',
  }
}
